import type { AxiosError, AxiosResponse } from "axios";
import type {
  BoardWiseTicketStats,
  GetBoardWiseTicketStatsInputProps,
  GetReportGraphInputProps,
  GetReportTableInputProps,
  GetTicketFeedbackStatsInputProps,
  SendAsEmailInputType,
  TicketFeedbackStats,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";
import {
  reportMetadataDummyData,
  reportsListDummyData,
} from "./reportDummyData";

const BASE_URL = import.meta.env.VITE_APP_REPORTS_BASE_URL;

/**
 * `resource` as the reports service wants it. Lists go as CSV (`resource=id1,id2`),
 * matching the rest of the API layer, and an empty selection is dropped rather
 * than sent blank — the service reads `resource=` as "no filter at all", which
 * would quietly widen a cleared picker to the whole tenant.
 *
 * The "all" / "assigned" / "unassigned" keywords pass through as-is. Never mix a
 * keyword into a list: the service lets the keyword win, so appending "all" to a
 * stale selection silently ignores the ids.
 */
const toResourceParam = (
  resource?: string | Array<string>,
): string | undefined => {
  if (Array.isArray(resource)) {
    return resource.length ? resource.join(",") : undefined;
  }
  return resource || undefined;
};
export const getReportsList = async () => {
  try {
    const url = `${BASE_URL}/v1/reports/categories`;
    const response = await axiosClient.get(url);

    const responseData = response.data;
    if (responseData.success && responseData.statusCode === 200) {
      return responseData;
    } else {
      throw new Error(responseData?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getReportMetaData = async (
  reportName: string,
  timePeriod: string,
) => {
  try {
    let url = `${BASE_URL}/v1/reports/meta-data/${reportName}`;
    // if (reportName === "period-utilization-by-resource") { // name changed 24/6/25
    if (reportName === "work-hours-by-period") {
      url += `?interval=${timePeriod}`;
    }
    const response = await axiosClient.get(url);
    const responseData = response.data?.data;
    if (response?.data.success && response?.data.statusCode === 200) {
      return responseData;
    } else {
      throw new Error(responseData?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getReportGraphData = async ({
  reportName,
  timePeriod,
  timezone,
  serviceType,
  customer,
  resource,
  serviceTypeFilter,
  filterAvailable,
  method = "GET",
}: GetReportGraphInputProps) => {
  const url = `${BASE_URL}/v1/reports/${serviceType}/${reportName}`;
  const resourceParam = toResourceParam(resource);
  const params = {
    time_zone: timezone,
    time_period: timePeriod,
    ...(customer && filterAvailable?.includes("Customer") ? { customer } : {}),
    ...(resourceParam && filterAvailable?.includes("Resource")
      ? { resource: resourceParam }
      : {}),
    ...(serviceTypeFilter ? { service_type: serviceTypeFilter } : {}),
  };

  // POST is purely a different transport for the same read — same params, same
  // response. It exists because `tickets-by-priority` only honours `resource` on
  // POST, and because a wide selection outgrows a query string.
  const response =
    method === "POST"
      ? await axiosClient.post(url, params)
      : await axiosClient.get(url, { params });

  // Every route in this service answers HTTP 200, failures included — the real
  // status is in the body.
  if (response?.data?.success && response?.data?.statusCode === 200) {
    return response.data?.data;
  }
  throw new Error(response?.data?.message);
};

export const getReportTableData = async ({
  reportName,
  timePeriod,
  timezone,
  serviceType,
  customer,
  resource,
  serviceTypeFilter,
  filterAvailable,
}: GetReportTableInputProps) => {
  const url = `${BASE_URL}/v1/reports/table/${serviceType}/${reportName}`;
  const resourceParam = toResourceParam(resource);
  // Resource and customer are mutually exclusive here — resource wins, which is
  // the behaviour every caller has relied on.
  const scope =
    filterAvailable.includes("Resource") && resourceParam
      ? { resource: resourceParam }
      : filterAvailable.includes("Customer") && customer
        ? { customer }
        : {};

  const response = await axiosClient.get(url, {
    params: {
      time_zone: timezone,
      time_period: timePeriod,
      ...scope,
      ...(serviceTypeFilter ? { service_type: serviceTypeFilter } : {}),
    },
  });

  const responseData = response.data?.data;
  if (responseData) {
    return responseData;
  }
  throw new Error(response.data?.message);
};

/**
 * Rated / unrated ticket split with per-rating counts. `resource` scopes the
 * stats to a set of resources, or to every one of them with the "all" keyword —
 * left empty it covers the whole tenant, unassigned work included.
 */
export const getTicketFeedbackStats = async ({
  timePeriod,
  timezone,
  resource,
}: GetTicketFeedbackStatsInputProps) => {
  try {
    const url = `${BASE_URL}/v1/reports/stats/ticket-feedback`;
    const resourceParam = toResourceParam(resource);
    const response = await axiosClient.get(url, {
      params: {
        time_period: timePeriod,
        time_zone: timezone,
        ...(resourceParam ? { resource: resourceParam } : {}),
      },
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data as TicketFeedbackStats;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Per-board ticket counts split by priority. `resource` scopes the stats to a
 * set of resources, or to every one of them with the "all" keyword — left empty
 * it covers the whole tenant, unassigned work included.
 *
 * Counts are a union: a ticket two selected resources share is counted once, so
 * a combined total is never the sum of the individual ones.
 */
export const getBoardWiseTicketStats = async ({
  timePeriod,
  timezone,
  resource,
}: GetBoardWiseTicketStatsInputProps) => {
  try {
    const url = `${BASE_URL}/v1/reports/stats/board-wise-tickets`;
    const resourceParam = toResourceParam(resource);
    const response = await axiosClient.get(url, {
      params: {
        time_period: timePeriod,
        time_zone: timezone,
        ...(resourceParam ? { resource: resourceParam } : {}),
      },
    });
    const resData = response.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData.data as BoardWiseTicketStats;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const sendAsEmail = async ({
  customerId,
  to,
  body,
  subject,
  reportName,
  reportType,
  file,
}: SendAsEmailInputType) => {
  try {
    const formData = new FormData();
    customerId && formData.append("customer_id", customerId);
    // formData.append("to", JSON.stringify([`${to}`]));
    formData.append("to", JSON.stringify([...to]));
    formData.append("email_body", body);
    formData.append("subject", subject);
    formData.append("report_name", reportName);
    formData.append("report_type", reportType);
    formData.append("files", file);
    const url = `${BASE_URL}/v1/reports/send-email`;
    const response = await axiosClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const responseData = response.data?.data;
    if (responseData) {
      return responseData;
    } else {
      throw new Error(responseData?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};
